import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { StyledTableRow, StyledTableHeader, ImagePreview } from './StyledComponents';
import { getImageUrl } from './lookupService';

const DataTable = ({ headers, data, onEdit, onDelete, tableType, isLoading }) => {
  const formatCellValue = (value, header, rowData) => {
    if (header === 'Sr No.') {
      return data.indexOf(rowData) + 1;
    }

    if (header === 'Display Image' && value) {
      const imageUrl = getImageUrl(value);
      return imageUrl ? <ImagePreview src={imageUrl} alt="Preview" /> : 'No Image';
    }

    if (header === 'Status') {
      const statusValue = Number(value);
      return (
        <Chip
          label={statusValue === 1 ? 'Active' : 'Inactive'}
          color={statusValue === 1 ? 'success' : 'default'}
          size="small"
        />
      );
    }

    if (header === 'Update at' && value) {
      return new Date(value).toLocaleDateString();
    }

    // For Topic column in sub-topics, show better message if missing
    if (header === 'Topic' && tableType === 'question-subtopic' && !value) {
      return <em style={{ color: '#888' }}>No topic assigned</em>;
    }

    return value || '-';
  };

  const getFieldNameForHeader = (header, tableType) => {
    const fieldMappings = {
      'difficulty': {
        'Level Name': 'level_name',
        'Display Image': 'display_image',
        'Base Time': 'base_time',
        'Base Points': 'base_points', 
        'Quick Times': 'quick_time_bonus_multiplier',
        'Late Times': 'late_time_bonus_multiplier',
        'Wrong Times': 'wrong_time_bonus_multiplier',
        'Wrong Penalty': 'wrong_penalty_time_multiplier',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'question-type': {
        'Type Name': 'type_name',
        'Display Name': 'display_name',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'answer-type': {
        'Type Name': 'type_name',
        'Display Name': 'display_name',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'question-mood': {
        'Mood Name': 'mood_name',
        'Display Name': 'display_name',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'question-topic': {
        'Topic Name': 'topic_name',
        'Display Name': 'display_name',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'question-subtopic': {
        'Topic': 'topic_display_name',
        'Sub-Topic Name': 'subtopic_name',
        'Display Name': 'display_name',
        'Status': 'status',
        'Update at': 'created_at'
      },
      'question-bank': {
        'Bank Name': 'bank_name',
        'Status': 'status',
        'Created at': 'created_at'
      },
      'games': {
        'Game Name': 'game_name',
        'Description': 'game_description',
        'Status': 'status',
        'Created at': 'created_at'
      },
      'games-datestamps': {
        'Game Name': 'game_name',
        'Date Stamp Name': 'date_stamp_name',
        'Description': 'description',
        'Status': 'status',
        'Created at': 'created_at'
      }
    };
    return fieldMappings[tableType]?.[header] || header.toLowerCase().replace(/\s+/g, '_');
  };

  const getRowId = (rowData) => {
    const idFields = {
      'difficulty': 'difficulty_id',
      'question-type': 'question_type_id',
      'answer-type': 'answer_type_id',
      'question-mood': 'mood_type_id',
      'question-topic': 'topic_type_id',
      'question-subtopic': 'subtopic_type_id',
      'question-bank': 'bank_id',
      'games': 'games_lookup_id',
      'games-datestamps': 'games_datestamps_lookups_id'
    };
    return rowData[idFields[tableType]];
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A' }}>
      <Table>
        <TableHead>
          <StyledTableHeader>
            {headers.map((header) => (
              <TableCell key={header} sx={{ color: 'white', fontWeight: 'bold' }}>
                {header}
              </TableCell>
            ))}
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
          </StyledTableHeader>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length + 1} sx={{ color: '#ccc', textAlign: 'center', py: 4 }}>
                No data available
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => (
              <StyledTableRow key={index}>
                {headers.map((header) => {
                  const fieldName = getFieldNameForHeader(header, tableType);
                  let cellValue = row[fieldName];
                  
                  // For sub-topics Topic column, handle the joined topic name
                  if (tableType === 'question-subtopic' && header === 'Topic') {
                    // The backend returns topic_display_name from the JOIN
                    cellValue = row.topic_display_name || row['topic_display_name'] || null;
                    // If still null, try to get from topic_name as fallback
                    if (!cellValue) {
                      cellValue = row.topic_name || null;
                    }
                  }
                  
                  return (
                    <TableCell key={header} sx={{ color: 'white' }}>
                      {formatCellValue(cellValue, header, row)}
                    </TableCell>
                  );
                })}
                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(row)}
                      sx={{ color: '#4CAF50' }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(getRowId(row))}
                      sx={{ color: '#f44336' }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
